import React, { useState, useEffect } from 'react';
import { useRBAC } from '../../../contexts/RBACContext';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';

// Icon components
const CodeIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const NameIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const DescriptionIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
  </svg>
);

const RoleForm = ({ role, onSuccess, onCancel }) => {
  const { 
    roles, 
    createRole, 
    updateRole, 
    loading, 
    rbacUtils 
  } = useRBAC();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    level: 6,
    parentCode: '',
    conditions: {},
    metadata: {}
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!role;

  useEffect(() => {
    if (role) {
      setFormData({
        code: role.code || '',
        name: role.name || '',
        description: role.description || '',
        level: role.level || 6,
        parentCode: role.parent?.code || '',
        conditions: role.conditions || {},
        metadata: role.metadata || {}
      });
    }
  }, [role]);

  const validateForm = () => {
    const newErrors = {};

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Role code is required';
    } else if (!/^[A-Z_]+$/.test(formData.code)) {
      newErrors.code = 'Role code must contain only uppercase letters and underscores';
    } else if (!isEditing && roles.some(r => r.code === formData.code)) {
      newErrors.code = 'Role code already exists';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Role name must be at least 3 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    // Level validation
    if (!formData.level || formData.level < 1 || formData.level > 10) {
      newErrors.level = 'Level must be between 1 and 10';
    }

    // Parent role validation
    if (formData.parentCode) {
      const parentRole = roles.find(r => r.code === formData.parentCode);
      if (!parentRole) {
        newErrors.parentCode = 'Selected parent role does not exist';
      } else if (parentRole.level >= formData.level) {
        newErrors.parentCode = 'Parent role level must be lower than this role level';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const roleData = {
        ...formData,
        parentCode: formData.parentCode || undefined
      };

      let result;
      if (isEditing) {
        result = await updateRole(role._id, roleData);
      } else {
        result = await createRole(roleData);
      }

      if (result.success) {
        onSuccess();
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableParentRoles = roles.filter(r => 
    r.level < formData.level && r.code !== formData.code
  );

  const levelOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
      <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-2xl border border-gray-100 max-h-[95vh] overflow-y-auto">
        {/* Compact Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">
                {isEditing ? 'Edit Role' : 'Create Role'}
              </h3>
            </div>
            <button
              onClick={onCancel}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4">
        {errors.submit && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Main Fields Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Role Code */}
              <TextInput
                label="Role Code"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                placeholder="CUSTOM_MANAGER"
                icon={CodeIcon}
                error={errors.code}
                required
                variant="input"
                disabled={isEditing}
                helperText={`Uppercase letters and underscores only${isEditing ? ' • Cannot be changed' : ''}`}
              />

              {/* Role Name */}
              <TextInput
                label="Role Name"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Custom Manager"
                icon={NameIcon}
                error={errors.name}
                required
                variant="input"
              />

              {/* Description */}
              <TextInput
                label="Description"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the role's purpose and responsibilities..."
                icon={DescriptionIcon}
                error={errors.description}
                required
                variant="textarea"
                rows={4}
                maxLength={500}
                showCharCount={true}
              />
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Hierarchy Level */}
              <div>
                <label htmlFor="level" className="block text-sm font-semibold text-gray-700 mb-2">
                  Hierarchy Level *
                </label>
                <CustomSelect
                  name="level"
                  id="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  options={levelOptions.map(level => ({
                    value: level,
                    label: `Level ${level} - ${rbacUtils.getRoleLevelName(level)}`
                  }))}
                  placeholder="Select hierarchy level"
                  className={errors.level ? 'ring-red-300 focus:ring-red-500 bg-red-50' : ''}
                  size="md"
                />
                {errors.level && <p className="mt-2 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.level}
                </p>}
                <p className="mt-1 text-xs text-gray-500 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Lower numbers = higher authority
                </p>
              </div>

              {/* Parent Role UI removed — permissions use Role.inherits on system roles, not parentCode */}
            </div>
          </div>

          {/* Compact Preview */}
          {formData.code && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm font-semibold text-indigo-900">Preview:</span>
                </div>
                <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-indigo-500"></div>
                  Level {formData.level}
                </div>
              </div>
              <div className="mt-2">
                <div className="font-mono text-sm text-indigo-800 bg-white bg-opacity-60 rounded px-2 py-1">
                  {formData.code}
                </div>
                {formData.name && (
                  <div className="text-xs text-indigo-700 mt-1">{formData.name}</div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading.roles}
              className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isEditing ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M12 4v16m8-8H4"} />
                  </svg>
                  {isEditing ? 'Update' : 'Create'}
                </div>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default RoleForm;